/**
 * Planning engine algorithms (Doc 19). Deterministic, auditable.
 * Produces constraint check results, divergence payloads, and optional bucket materialization.
 * All materializations stamp engine_version.
 */

import { getSupabaseClient } from './supabaseClient';
import { getWorkUnitReadiness, type BlockerType } from './planningQueries';
import { evaluatePlanVersionRpc } from './planningRpcService';
import {
  CHECK_DEPENDENCY_CYCLE,
  CHECK_DEPENDENCIES_SATISFIED,
  CHECK_BUCKETS_MATERIALIZED,
  CHECK_CAPACITY_FEASIBLE,
  CHECK_ASSUMPTIONS_VALID,
  CHECK_INITIATIVES_NOT_DROPPED,
  CHECK_VERSION_NOT_EMPTY,
  CHECK_NO_CAPACITY_MODEL,
} from './planningConstraintKeys';

export const ENGINE_VERSION = 'doc19-v1';

// ---------------------------------------------------------------------------
// 1. Time Buckets (Doc 19 §1)
// ---------------------------------------------------------------------------

export type TimeBucketGranularity = 'week' | 'sprint' | 'month';

export interface TimeBucket {
  time_bucket_start: string; // ISO date
  time_bucket_end: string;
}

/** Monday of the week containing the given date (ISO). */
function getMonday(d: Date): Date {
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.getFullYear(), d.getMonth(), diff);
}

/** Generate inclusive time buckets (Doc 19 §1.2). Default granularity: week (Mon–Sun). */
export function generateTimeBuckets(
  startDate: string,
  endDate: string,
  granularity: TimeBucketGranularity = 'week'
): TimeBucket[] {
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (start > end) return [];

  const buckets: TimeBucket[] = [];
  if (granularity === 'week') {
    let cur = new Date(getMonday(start));
    if (cur < start) cur = new Date(start);
    while (cur <= end) {
      const bucketEnd = new Date(cur);
      bucketEnd.setDate(bucketEnd.getDate() + 6);
      const bucketEndClamped = bucketEnd > end ? end : bucketEnd;
      buckets.push({
        time_bucket_start: cur.toISOString().slice(0, 10),
        time_bucket_end: bucketEndClamped.toISOString().slice(0, 10),
      });
      cur.setDate(cur.getDate() + 7);
    }
  } else if (granularity === 'month') {
    const y = start.getFullYear();
    const m = start.getMonth();
    const cur = new Date(y, m, 1);
    while (cur <= end) {
      const lastDay = new Date(cur.getFullYear(), cur.getMonth() + 1, 0);
      const bucketEndClamped = lastDay > end ? end : lastDay;
      buckets.push({
        time_bucket_start: cur.toISOString().slice(0, 10),
        time_bucket_end: bucketEndClamped.toISOString().slice(0, 10),
      });
      cur.setMonth(cur.getMonth() + 1);
    }
  } else {
    // sprint: treat as 2-week; align to start
    const cur = new Date(start);
    while (cur <= end) {
      const bucketEnd = new Date(cur);
      bucketEnd.setDate(bucketEnd.getDate() + 13);
      const bucketEndClamped = bucketEnd > end ? end : bucketEnd;
      buckets.push({
        time_bucket_start: cur.toISOString().slice(0, 10),
        time_bucket_end: bucketEndClamped.toISOString().slice(0, 10),
      });
      cur.setDate(cur.getDate() + 14);
    }
  }
  return buckets;
}

/** Overlap days between [a1,a2] and [b1,b2] (inclusive dates). */
export function overlapDays(a1: string, a2: string, b1: string, b2: string): number {
  const start = Math.max(new Date(a1).getTime(), new Date(b1).getTime());
  const end = Math.min(new Date(a2).getTime(), new Date(b2).getTime());
  if (end < start) return 0;
  return Math.ceil((end - start) / (24 * 60 * 60 * 1000)) + 1;
}

/** Prorate amount by overlap days (Doc 19 §1.3). totalDays = span of the range; overlapDays = overlap with bucket. */
export function prorateByOverlap(amount: number, totalDays: number, overlapDays: number): number {
  if (totalDays <= 0) return 0;
  return (amount * overlapDays) / totalDays;
}

// ---------------------------------------------------------------------------
// 2–4. Bucket materialization (Doc 19 §2–4) — minimal v2 path
// ---------------------------------------------------------------------------

const TABLE_CAPABILITY_BUCKETS = 'capability_buckets';
const TABLE_BUCKET_CAPACITY_WINDOWS = 'bucket_capacity_windows';
const TABLE_BUCKET_ALLOCATIONS = 'bucket_allocations';
const TABLE_CAPACITY_ALLOCATIONS = 'capacity_allocations';

/** Default hours per week per resource when no capacity calendar exists. */
const DEFAULT_HOURS_PER_WEEK = 40;

/**
 * Ensure one default capability bucket exists for the plan version and materialize
 * capacity windows + allocations so BucketsMaterialized and capacity checks can run.
 * Idempotent: skips if buckets already exist.
 */
/**
 * How the scenario this version was committed from accounts for uncertainty.
 * Absent scenario or column means 'buffer', which is what the engine has always
 * done.
 */
async function uncertaintyBasisForPlanVersion(planVersionId: string): Promise<'buffer' | 'work_type'> {
  const supabase = getSupabaseClient();
  const { data: version } = await supabase
    .from('plan_versions')
    .select('payload')
    .eq('id', planVersionId)
    .maybeSingle();
  const scenarioId = (version?.payload as { scenario_id?: string } | null)?.scenario_id;
  if (!scenarioId) return 'buffer';
  const { data: scenario } = await supabase
    .from('planning_scenarios')
    .select('uncertainty_basis')
    .eq('id', scenarioId)
    .maybeSingle();
  return scenario?.uncertainty_basis === 'work_type' ? 'work_type' : 'buffer';
}

/** One resource type's supply, as the committing scenario had it. */
interface ScenarioCapacityLine {
  resourceType: string;
  /** Weekly hours × the scenario's planning weeks, after its capacity buffer. */
  effectiveHours: number;
}

/**
 * The capacity the scenario this version was committed from was working with.
 *
 * A work-units plan is that scenario's decision made official, so the plan of
 * record should be judged against the same supply the decision was made with —
 * including the scenario's buffer, which the plan side otherwise knows nothing
 * about. Returns [] when the version came from no scenario, or that scenario
 * has no capacity: the caller then writes no supply rather than inventing one.
 */
async function scenarioCapacityForPlanVersion(planVersionId: string): Promise<ScenarioCapacityLine[]> {
  const supabase = getSupabaseClient();
  const { data: version } = await supabase
    .from('plan_versions')
    .select('payload')
    .eq('id', planVersionId)
    .maybeSingle();
  const scenarioId = (version?.payload as { scenario_id?: string } | null)?.scenario_id;
  if (!scenarioId) return [];

  const [{ data: scenario }, { data: rows }] = await Promise.all([
    supabase
      .from('planning_scenarios')
      .select('planning_weeks, capacity_buffer_percent, uncertainty_basis')
      .eq('id', scenarioId)
      .maybeSingle(),
    supabase
      .from('planning_capacity')
      .select('resource_type, weekly_capacity_hours')
      .eq('scenario_id', scenarioId),
  ]);

  const weeks = Number(scenario?.planning_weeks) > 0 ? Number(scenario?.planning_weeks) : 12;
  // A scenario accounts for uncertainty one way or the other. Under
  // 'work_type' the allowance is on the demand side, in the recipes'
  // multipliers, so the supply buffer must not also be taken — charging the
  // same uncertainty twice is what put the engine and the plan at odds.
  const basis = scenario?.uncertainty_basis === 'work_type' ? 'work_type' : 'buffer';
  const rawBuffer = basis === 'buffer' ? Number(scenario?.capacity_buffer_percent) : 0;
  // Stored as a fraction (0.2 = 20%); tolerate a whole-number percent.
  const buffer = !Number.isFinite(rawBuffer) || rawBuffer <= 0 ? 0 : rawBuffer > 1 ? rawBuffer / 100 : rawBuffer;

  const byType = new Map<string, number>();
  for (const r of (rows ?? []) as { resource_type: string | null; weekly_capacity_hours: number | null }[]) {
    const type = (r.resource_type ?? '').trim() || 'default';
    byType.set(type, (byType.get(type) ?? 0) + (Number(r.weekly_capacity_hours) || 0));
  }
  return [...byType.entries()]
    .map(([resourceType, weekly]) => ({
      resourceType,
      effectiveHours: weekly * weeks * (1 - buffer),
    }))
    .filter((l) => l.effectiveHours > 0);
}

export async function materializeBucketsForPlanVersion(planVersionId: string): Promise<{
  data: { bucketId: string; windowsCreated: number; allocationsCreated: number } | null;
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const { data: existing } = await supabase
      .from(TABLE_CAPABILITY_BUCKETS)
      .select('id')
      .eq('plan_version_id', planVersionId)
      .limit(1)
      .maybeSingle();
    // Demand (bucket allocations) is materialized once. Supply (capacity
    // windows) has to be re-checked every run: capacity is added on the
    // Capacity page AFTER the plan version exists, and the supply window used
    // to be written only in the branch that creates the bucket — so allocating
    // people to a plan never reached its supply, and the capacity checks went
    // on reporting that the plan had none. Observed on a plan with 10
    // allocations and 0 supply hours.
    const { count: existingAllocs } = existing?.id
      ? await supabase
          .from(TABLE_BUCKET_ALLOCATIONS)
          .select('id', { count: 'exact', head: true })
          .eq('plan_version_id', planVersionId)
      : { count: 0 };

    let bucketId = existing?.id ?? null;
    if (!bucketId) {
      const { data: bucket, error: bucketErr } = await supabase
        .from(TABLE_CAPABILITY_BUCKETS)
        .insert({
          plan_version_id: planVersionId,
          bucket_type: 'default',
          name: 'Default capacity',
          payload: { engine_version: ENGINE_VERSION },
        })
        .select('id')
        .single();
      if (bucketErr || !bucket?.id) return { data: null, error: bucketErr?.message ?? 'Failed to create bucket' };
      bucketId = bucket.id;
    }

    // Supply, in order of authority:
    //
    //   1. Capacity allocated to this plan version by a person on the Capacity
    //      page — a deliberate entry, so it wins.
    //   2. Otherwise the capacity of the scenario this version was committed
    //      from. A work-units plan IS the scenario's decision made official, so
    //      it should stand on the same numbers that decision was made with —
    //      per resource type, and after the scenario's buffer. Re-keying them by
    //      hand was not just friction: the plan was being graded against a
    //      different supply than the engine used. Observed on the Sightline Q4
    //      plan — the engine deferred six priorities against 3,840 effective
    //      engineering hours (400/wk × 12 weeks, less a 20% buffer) while the
    //      plan measured the same work against 4,800 undifferentiated hours.
    //   3. Otherwise nothing. No capacity means no capacity — this used to fall
    //      back to DEFAULT_HOURS_PER_WEEK × 12 = 480 hours, so a plan nobody had
    //      staffed was measured against a figure the product made up.
    //      runCapacityFeasibilityChecks then reports NoCapacityModel.
    let windowsCreated = 0;
    const bucketIdByProfile = new Map<string, string>();
    const { data: existingWindows } = await supabase
      .from(TABLE_BUCKET_CAPACITY_WINDOWS)
      .select('id')
      .eq('capability_bucket_id', bucketId)
      .limit(1);
    if (!(existingWindows ?? []).length) {
      const { data: capAllocs } = await supabase
        .from(TABLE_CAPACITY_ALLOCATIONS)
        .select('resource_identifier, allocation_percentage')
        .eq('plan_version_id', planVersionId);
      const resources = (capAllocs ?? []) as { resource_identifier: string; allocation_percentage: number }[];

      const now = new Date();
      const windowStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const windowEnd = new Date(windowStart);
      windowEnd.setMonth(windowEnd.getMonth() + 3);

      if (resources.length > 0) {
        const hoursPerWeek = resources.reduce(
          (sum, r) => sum + (DEFAULT_HOURS_PER_WEEK * (Number(r.allocation_percentage) || 100) / 100),
          0,
        );
        const weeks = 12;
        const { error: winErr } = await supabase.from(TABLE_BUCKET_CAPACITY_WINDOWS).insert({
          capability_bucket_id: bucketId,
          window_start: windowStart.toISOString(),
          window_end: windowEnd.toISOString(),
          capacity_hours: hoursPerWeek * weeks,
        });
        if (winErr) return { data: null, error: winErr.message };
        windowsCreated = 1;
      } else {
        const scenario = await scenarioCapacityForPlanVersion(planVersionId);
        for (const line of scenario) {
          // One bucket per resource type, so engineering demand is judged
          // against engineering supply and not against the company total.
          let profileBucketId = bucketId;
          if (line.resourceType !== 'default') {
            const { data: rtBucket } = await supabase
              .from(TABLE_CAPABILITY_BUCKETS)
              .insert({
                plan_version_id: planVersionId,
                bucket_type: 'resource_type',
                name: line.resourceType,
                payload: { engine_version: ENGINE_VERSION, resource_type: line.resourceType, inherited_from_scenario: true },
              })
              .select('id')
              .single();
            if (rtBucket?.id) profileBucketId = rtBucket.id as string;
          }
          bucketIdByProfile.set(line.resourceType, profileBucketId);
          const { error: winErr } = await supabase.from(TABLE_BUCKET_CAPACITY_WINDOWS).insert({
            capability_bucket_id: profileBucketId,
            window_start: windowStart.toISOString(),
            window_end: windowEnd.toISOString(),
            capacity_hours: line.effectiveHours,
          });
          if (winErr) return { data: null, error: winErr.message };
          windowsCreated += 1;
        }
      }
    }

    if ((existingAllocs ?? 0) > 0) {
      return { data: { bucketId, windowsCreated, allocationsCreated: 0 }, error: null };
    }

    const { data: initIds } = await supabase
      .from('initiatives')
      .select('id, item_id, work_profile')
      .eq('plan_version_id', planVersionId);
    const initiatives = (initIds ?? []) as { id: string; item_id: string | null; work_profile: string | null }[];
    const initiativeIds = initiatives.map((r) => r.id);
    const { data: wuRows } = await supabase
      .from('work_units')
      .select('id, initiative_id, estimated_effort_hours, resolved_recipe_id')
      .in('initiative_id', initiativeIds);
    const workUnits = (wuRows ?? []) as { id: string; initiative_id: string; estimated_effort_hours: number | null; resolved_recipe_id: string | null }[];

    // Which resource type each initiative's work draws on. The plan's own
    // work_profile when it has one; otherwise the profile the engine recorded on
    // the work units behind that priority — the engine is what decided this work
    // fit, so its answer is the one to measure against.
    const profileByInitiative = new Map<string, string>();
    if (bucketIdByProfile.size > 0) {
      for (const init of initiatives) {
        if (init.work_profile) profileByInitiative.set(init.id, init.work_profile);
      }
      const unresolved = initiatives.filter((i) => i.item_id && !profileByInitiative.has(i.id));
      if (unresolved.length > 0) {
        const { data: engineUnits } = await supabase
          .from('planning_work_units')
          .select('priority_id, work_profile')
          .in('priority_id', unresolved.map((i) => i.item_id as string));
        const profileByPriority = new Map<string, string>();
        for (const row of (engineUnits ?? []) as { priority_id: string | null; work_profile: string | null }[]) {
          if (row.priority_id && row.work_profile && !profileByPriority.has(row.priority_id)) {
            profileByPriority.set(row.priority_id, row.work_profile);
          }
        }
        for (const init of unresolved) {
          const p = profileByPriority.get(init.item_id as string);
          if (p) profileByInitiative.set(init.id, p);
        }
      }
    }

    // Batch-fetch uncertainty_multiplier from execution_recipe_templates (Canon Doc 21 §4.3).
    // allocation_amount = estimated_effort_hours × uncertainty_multiplier so capacity feasibility
    // checks use adjusted demand, not raw estimates.
    //
    // Only under the 'work_type' basis. A 'buffer' scenario has already taken its
    // uncertainty allowance out of supply, so multiplying demand here as well
    // charges it twice — and, because the engine applied only the buffer and this
    // side applied only the multipliers, the two reached different verdicts about
    // identical work.
    const uncertaintyBasis = await uncertaintyBasisForPlanVersion(planVersionId);
    const recipeIds =
      uncertaintyBasis === 'work_type'
        ? ([...new Set(workUnits.map((w) => w.resolved_recipe_id).filter(Boolean))] as string[])
        : [];
    const recipeMultipliers = new Map<string, number>();
    if (recipeIds.length) {
      const { data: recipes } = await supabase
        .from('execution_recipe_templates')
        .select('id, uncertainty_multiplier')
        .in('id', recipeIds);
      for (const r of (recipes ?? []) as { id: string; uncertainty_multiplier: number }[]) {
        recipeMultipliers.set(r.id, Number(r.uncertainty_multiplier) >= 1 ? Number(r.uncertainty_multiplier) : 1.0);
      }
    }

    let allocCount = 0;
    for (const wu of workUnits) {
      const baseHours = Number(wu.estimated_effort_hours) || 0;
      if (baseHours <= 0) continue;
      const multiplier = wu.resolved_recipe_id ? (recipeMultipliers.get(wu.resolved_recipe_id) ?? 1.0) : 1.0;
      const adjustedHours = baseHours * multiplier;
      const profile = profileByInitiative.get(wu.initiative_id);
      const targetBucketId = (profile && bucketIdByProfile.get(profile)) || bucketId;
      const { error: allocErr } = await supabase.from(TABLE_BUCKET_ALLOCATIONS).insert({
        capability_bucket_id: targetBucketId,
        plan_version_id: planVersionId,
        work_unit_id: wu.id,
        allocation_amount: adjustedHours,
      });
      if (!allocErr) allocCount++;
    }
    // Emit BUCKETS_MATERIALIZED audit event (Canon Doc 14 §4)
    await supabase.from('planning_audit_events').insert({
      event_type: 'BUCKETS_MATERIALIZED',
      plan_version_id: planVersionId,
      payload: {
        bucket_id: bucketId,
        windows_created: windowsCreated,
        allocations_created: allocCount,
        engine_version: ENGINE_VERSION,
      },
    }).then(() => {}).catch(() => {});

    return { data: { bucketId, windowsCreated, allocationsCreated: allocCount }, error: null };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Bucket materialization failed' };
  }
}

// ---------------------------------------------------------------------------
// 5. Constraint evaluation (Doc 19 §5) — deterministic order
// ---------------------------------------------------------------------------

export interface ConstraintCheckResult {
  check_key: string;
  result: 'pass' | 'warn' | 'fail';
  payload?: {
    severity?: number;
    explanation?: string;
    remediation?: string;
    target_refs?: string[];
    [key: string]: unknown;
  };
}

/** 1) Structural: dependency cycle, missing refs. */
async function runStructuralChecks(planVersionId: string): Promise<ConstraintCheckResult[]> {
  const supabase = getSupabaseClient();
  const { data: initRows } = await supabase.from('initiatives').select('id').eq('plan_version_id', planVersionId);
  const initiativeIds = (initRows ?? []).map((r: { id: string }) => r.id);
  if (initiativeIds.length === 0) {
    return [{ check_key: CHECK_DEPENDENCY_CYCLE, result: 'pass', payload: { explanation: 'No work units' } }];
  }
  const { data: wuRows } = await supabase.from('work_units').select('id').in('initiative_id', initiativeIds);
  const wuIds = new Set((wuRows ?? []).map((r: { id: string }) => r.id));
  if (wuIds.size === 0) {
    return [{ check_key: CHECK_DEPENDENCY_CYCLE, result: 'pass', payload: { explanation: 'No work units' } }];
  }
  const { data: linkRows } = await supabase.from('dependency_links').select('from_work_unit_id, to_work_unit_id');
  const links = (linkRows ?? []).filter(
    (l: { from_work_unit_id: string; to_work_unit_id: string }) => wuIds.has(l.from_work_unit_id) && wuIds.has(l.to_work_unit_id)
  ) as { from_work_unit_id: string; to_work_unit_id: string }[];

  const inDegree = new Map<string, number>();
  const outEdges = new Map<string, string[]>();
  wuIds.forEach((id) => {
    inDegree.set(id, 0);
    outEdges.set(id, []);
  });
  for (const l of links) {
    inDegree.set(l.to_work_unit_id, (inDegree.get(l.to_work_unit_id) ?? 0) + 1);
    outEdges.get(l.from_work_unit_id)!.push(l.to_work_unit_id);
  }
  const queue = Array.from(wuIds).filter((id) => inDegree.get(id) === 0);
  const order: string[] = [];
  while (queue.length > 0) {
    const id = queue.shift()!;
    order.push(id);
    for (const toId of outEdges.get(id) ?? []) {
      const d = (inDegree.get(toId) ?? 0) - 1;
      inDegree.set(toId, d);
      if (d === 0) queue.push(toId);
    }
  }
  if (order.length !== wuIds.size) {
    return [
      {
        check_key: CHECK_DEPENDENCY_CYCLE,
        result: 'fail',
        payload: {
          severity: 1,
          explanation: 'Circular dependency in work unit graph',
          remediation: 'Remove or reorder dependency links to break the cycle.',
        },
      },
    ];
  }
  return [{ check_key: CHECK_DEPENDENCY_CYCLE, result: 'pass', payload: { explanation: 'DAG is acyclic' } }];
}

/** 2) Dependency feasibility (readiness: all deps satisfiable). */
async function runDependencyFeasibilityChecks(planVersionId: string): Promise<ConstraintCheckResult[]> {
  const readinessRes = await getWorkUnitReadiness(planVersionId);
  const list = readinessRes.data ?? [];
  const notReady = list.filter((r) => !r.ready);
  if (notReady.length === 0) {
    return [{ check_key: CHECK_DEPENDENCIES_SATISFIED, result: 'pass', payload: { explanation: 'All work units dependency-satisfiable' } }];
  }
  const blockerCount = notReady.reduce((s, r) => s + r.blockers.length, 0);
  const severity = blockerCount > 5 ? 1 : blockerCount > 0 ? 0.5 : 0;

  // Readiness refuses a work unit for four different reasons, only one of which
  // is a dependency. Reporting all four as "blocked by dependencies" sent people
  // to reorder predecessors that did not exist — observed on a plan whose 12
  // work units were all held up by a missing capacity allocation. Name the
  // reason that is actually there.
  const byType = new Map<BlockerType, Set<string>>();
  for (const r of notReady) {
    const types = (r.structured_blockers ?? []).map((b) => b.type);
    for (const t of types.length > 0 ? types : (['dependency'] as BlockerType[])) {
      const set = byType.get(t) ?? new Set<string>();
      set.add(r.work_unit_id);
      byType.set(t, set);
    }
  }
  const phrasing: Record<BlockerType, { label: string; what: string; fix: string }> = {
    dependency: {
      label: 'Blocking dependency unresolved',
      what: 'are waiting on work that is not done, or not scheduled before them',
      fix: 'Complete or reorder predecessor work units; or adjust dependency types.',
    },
    capacity: {
      label: 'No capacity allocated to this plan',
      what: 'cannot be scheduled: no capacity has been allocated to this plan version',
      fix: 'Allocate capacity for this plan version on the Capacity page, then run the checks again.',
    },
    assumption_invalidated: {
      label: 'An assumption has been invalidated',
      what: 'are held up because an assumption this plan rests on has been invalidated',
      fix: 'Resolve the assumption, or record a tradeoff against it (Assumptions & Risks).',
    },
    structural: {
      label: 'Work under a dropped initiative',
      what: 'sit under an initiative that has been dropped',
      fix: 'Restore the initiative, or take its work units out of this version.',
    },
  };
  const present = ([...byType.entries()] as [BlockerType, Set<string>][]).sort((a, b) => b[1].size - a[1].size);
  const explanation = present
    .map(([t, ids]) => `${ids.size} work unit(s) ${phrasing[t].what}`)
    .join('; ');
  const remediation = present.map(([t]) => phrasing[t].fix).join(' ');
  const failureLabel = present.map(([t]) => phrasing[t].label).join('; ');

  return [
    {
      check_key: CHECK_DEPENDENCIES_SATISFIED,
      result: blockerCount > 0 ? 'fail' : 'pass',
      payload: {
        severity,
        explanation: explanation || `${notReady.length} work unit(s) are not ready`,
        remediation: remediation || 'Complete or reorder predecessor work units; or adjust dependency types.',
        failure_label: failureLabel || undefined,
        target_refs: notReady.slice(0, 10).map((r) => r.work_unit_id),
      },
    },
  ];
}

/** 3) Capability fit (Doc 19 §4.2, §5). Every work unit's required capabilities must be satisfiable by at least one eligible bucket. */
async function runCapabilityFitChecks(planVersionId: string): Promise<ConstraintCheckResult[]> {
  const supabase = getSupabaseClient();
  const { data: initRows } = await supabase.from('initiatives').select('id').eq('plan_version_id', planVersionId);
  const initiativeIds = (initRows ?? []).map((r: { id: string }) => r.id);
  if (initiativeIds.length === 0) {
    return [{ check_key: CHECK_BUCKETS_MATERIALIZED, result: 'pass', payload: { explanation: 'No initiatives; capability fit N/A' } }];
  }
  const { data: wuRows } = await supabase.from('work_units').select('id, title').in('initiative_id', initiativeIds);
  const workUnits = (wuRows ?? []) as { id: string; title: string | null }[];
  if (workUnits.length === 0) {
    return [{ check_key: CHECK_BUCKETS_MATERIALIZED, result: 'pass', payload: { explanation: 'No work units; capability fit N/A' } }];
  }
  const wuIds = workUnits.map((w) => w.id);
  const { data: reqRows } = await supabase
    .from('work_unit_capability_requirements')
    .select('work_unit_id, capability_id, min_level')
    .in('work_unit_id', wuIds);
  const requirements = (reqRows ?? []) as { work_unit_id: string; capability_id: string; min_level: number }[];
  if (requirements.length === 0) {
    return [{ check_key: CHECK_BUCKETS_MATERIALIZED, result: 'pass', payload: { explanation: 'No capability requirements defined; all work units fit' } }];
  }
  const { data: buckets } = await supabase.from(TABLE_CAPABILITY_BUCKETS).select('id').eq('plan_version_id', planVersionId);
  const bucketIds = (buckets ?? []).map((b: { id: string }) => b.id);
  if (bucketIds.length === 0) {
    return [
      {
        check_key: CHECK_BUCKETS_MATERIALIZED,
        result: 'fail',
        payload: {
          severity: 0.7,
          explanation: `${requirements.length} capability requirement(s) but no capability buckets materialized`,
          remediation: 'Materialize capability buckets (Doc 19 §2–3) or add bucket membership rules that cover required capabilities.',
          target_refs: [...new Set(requirements.map((r) => r.work_unit_id))],
        },
      },
    ];
  }
  const { data: ruleRows } = await supabase
    .from('bucket_membership_rules')
    .select('capability_bucket_id, payload')
    .in('capability_bucket_id', bucketIds);
  const rules = (ruleRows ?? []) as { capability_bucket_id: string; payload?: { capability_id?: string; min_level?: number } | null }[];
  const bucketCapabilities = new Map<string, Set<string>>();
  for (const r of rules) {
    const capId = r.payload?.capability_id;
    if (!capId) continue;
    const key = r.capability_bucket_id;
    if (!bucketCapabilities.has(key)) bucketCapabilities.set(key, new Set());
    bucketCapabilities.get(key)!.add(capId);
  }
  const unsatisfied: string[] = [];
  for (const req of requirements) {
    const hasBucket = bucketIds.some((bid) => {
      const caps = bucketCapabilities.get(bid);
      return caps?.has(req.capability_id);
    });
    if (!hasBucket) unsatisfied.push(req.work_unit_id);
  }
  const uniqueUnsatisfied = [...new Set(unsatisfied)];
  if (uniqueUnsatisfied.length === 0) {
    return [{ check_key: CHECK_BUCKETS_MATERIALIZED, result: 'pass', payload: { explanation: 'All work unit capability requirements satisfied by at least one bucket' } }];
  }
  return [
    {
      check_key: CHECK_BUCKETS_MATERIALIZED,
      result: 'fail',
      payload: {
        severity: 0.7,
        explanation: `${uniqueUnsatisfied.length} work unit(s) have capability requirements not satisfied by any bucket`,
        remediation: 'Add bucket membership rules for the required capabilities (Doc 19 §2.2) or reduce work unit capability requirements.',
        target_refs: uniqueUnsatisfied,
      },
    },
  ];
}

/**
 * Fetch work units for the plan version with their resolved recipes.
 * Used in capacity feasibility and recipe-pending checks (Canon Doc 21 §4.3).
 */
async function getWorkUnitsWithRecipes(planVersionId: string): Promise<{
  id: string;
  title: string | null;
  estimated_effort_hours: number | null;
  resolved_recipe_id: string | null;
  uncertainty_multiplier: number;
  work_type_ref: string | null;
}[]> {
  const supabase = getSupabaseClient();
  const { data: initRows } = await supabase.from('initiatives').select('id').eq('plan_version_id', planVersionId);
  const initiativeIds = (initRows ?? []).map((r: { id: string }) => r.id);
  if (!initiativeIds.length) return [];

  const { data: wuRows } = await supabase
    .from('work_units')
    .select('id, title, estimated_effort_hours, resolved_recipe_id')
    .in('initiative_id', initiativeIds);
  const workUnits = (wuRows ?? []) as { id: string; title: string | null; estimated_effort_hours: number | null; resolved_recipe_id: string | null }[];

  // Batch-fetch recipes for resolved work units
  const recipeIds = [...new Set(workUnits.map((w) => w.resolved_recipe_id).filter(Boolean))] as string[];
  const recipeMap = new Map<string, { uncertainty_multiplier: number; work_type_ref: string }>();
  if (recipeIds.length) {
    const { data: recipes } = await supabase
      .from('execution_recipe_templates')
      .select('id, uncertainty_multiplier, work_type_ref')
      .in('id', recipeIds);
    for (const r of (recipes ?? []) as { id: string; uncertainty_multiplier: number; work_type_ref: string }[]) {
      recipeMap.set(r.id, { uncertainty_multiplier: r.uncertainty_multiplier, work_type_ref: r.work_type_ref });
    }
  }

  return workUnits.map((w) => {
    const recipe = w.resolved_recipe_id ? recipeMap.get(w.resolved_recipe_id) : undefined;
    return {
      ...w,
      uncertainty_multiplier: recipe?.uncertainty_multiplier ?? 1.0,
      work_type_ref: recipe?.work_type_ref ?? null,
    };
  });
}

/** 4) Capacity feasibility — use bucket balance if buckets exist.
 *  Recipe uncertainty_multiplier is applied to estimated_effort_hours per Canon Doc 21 §4.3.
 */
async function runCapacityFeasibilityChecks(planVersionId: string): Promise<ConstraintCheckResult[]> {
  const supabase = getSupabaseClient();
  const { data: buckets } = await supabase.from(TABLE_CAPABILITY_BUCKETS).select('id, name').eq('plan_version_id', planVersionId);
  if (!buckets?.length) {
    return [{ check_key: CHECK_CAPACITY_FEASIBLE, result: 'warn', payload: { explanation: 'No capability buckets; capacity not evaluated' } }];
  }

  // The RECIPE_RESOLVED gate lives in runPolicyChecks, which is where the policy
  // gates are. This function used to emit a second, near-identical recipe.pending
  // row — and, worse, returned early on it, so a plan with any unassigned work
  // type never had its capacity evaluated at all and the duplicate was the only
  // thing anyone saw. Capacity demand comes from bucket allocations, not recipes,
  // so there is nothing here that needs a recipe to compute.
  const workUnits = await getWorkUnitsWithRecipes(planVersionId);

  const bucketIds = buckets.map((b: { id: string }) => b.id);
  const [winRes, allocRes] = await Promise.all([
    supabase.from(TABLE_BUCKET_CAPACITY_WINDOWS).select('capability_bucket_id, capacity_hours').in('capability_bucket_id', bucketIds),
    supabase.from(TABLE_BUCKET_ALLOCATIONS).select('capability_bucket_id, allocation_amount').eq('plan_version_id', planVersionId),
  ]);
  const supplyByBucket = new Map<string, number>();
  for (const w of (winRes.data ?? []) as { capability_bucket_id: string; capacity_hours: number }[]) {
    supplyByBucket.set(w.capability_bucket_id, (supplyByBucket.get(w.capability_bucket_id) ?? 0) + Number(w.capacity_hours));
  }

  // Nothing to judge demand against. Say that, rather than reporting an
  // overrun against a supply of zero as though someone had measured it.
  const totalSupply = [...supplyByBucket.values()].reduce((a, b) => a + b, 0);
  if (totalSupply <= 0) {
    return [
      {
        check_key: CHECK_NO_CAPACITY_MODEL,
        result: 'fail',
        payload: {
          severity: 0.8,
          explanation: 'No capacity has been allocated to this plan, so what it demands cannot be judged',
          remediation: 'Add capacity allocations for this plan version on the Capacity page, then run the checks again.',
          failure_label: 'No capacity allocated to this plan',
        },
      },
    ];
  }

  // Demand uses uncertainty-adjusted effort (Canon Doc 21 §4.3)
  // adjusted_effort = estimated_effort_hours × uncertainty_multiplier
  const demandByBucket = new Map<string, number>();
  for (const a of (allocRes.data ?? []) as { capability_bucket_id: string; allocation_amount: number }[]) {
    demandByBucket.set(a.capability_bucket_id, (demandByBucket.get(a.capability_bucket_id) ?? 0) + Number(a.allocation_amount));
  }

  // Build a note about uncertainty multipliers used for auditability
  const multiplierSummary = workUnits.length
    ? workUnits
        .filter((w) => w.uncertainty_multiplier !== 1.0)
        .map((w) => `${w.work_type_ref ?? 'unknown'} ×${w.uncertainty_multiplier}`)
        .join(', ')
    : '';

  let overCount = 0;
  for (const b of buckets as { id: string; name: string | null }[]) {
    const supply = supplyByBucket.get(b.id) ?? 0;
    const demand = demandByBucket.get(b.id) ?? 0;
    if (demand > supply && supply >= 0) overCount++;
  }
  if (overCount === 0) {
    return [{
      check_key: CHECK_CAPACITY_FEASIBLE,
      result: 'pass',
      payload: {
        explanation: `Demand within supply for all buckets${multiplierSummary ? `. Uncertainty multipliers applied: ${multiplierSummary}` : '.'}`,
      },
    }];
  }
  return [
    {
      check_key: CHECK_CAPACITY_FEASIBLE,
      result: 'fail',
      payload: {
        severity: 0.8,
        explanation: `${overCount} bucket(s) have demand exceeding supply${multiplierSummary ? ` (uncertainty multipliers applied: ${multiplierSummary})` : ''}`,
        remediation: 'Reduce scope, move dates, or add capacity (Doc 19 §5.3).',
      },
    },
  ];
}

/** 5) Policy constraints (Doc 19 §5). Minimum: every work unit must have estimated effort set and > 0.
 *  Also checks for recipe_pending work units (Canon Doc 21 §5.1). */
async function runPolicyChecks(planVersionId: string): Promise<ConstraintCheckResult[]> {
  const supabase = getSupabaseClient();
  const { data: initRows } = await supabase.from('initiatives').select('id').eq('plan_version_id', planVersionId);
  const initiativeIds = (initRows ?? []).map((r: { id: string }) => r.id);
  if (initiativeIds.length === 0) {
    return [{ check_key: CHECK_ASSUMPTIONS_VALID, result: 'pass', payload: { explanation: 'No initiatives; policy N/A' } }];
  }
  const { data: wuRows } = await supabase
    .from('work_units')
    .select('id, title, estimated_effort_hours, resolved_recipe_id')
    .in('initiative_id', initiativeIds);
  const workUnits = (wuRows ?? []) as { id: string; title: string | null; estimated_effort_hours: number | null; resolved_recipe_id: string | null }[];
  const results: ConstraintCheckResult[] = [];

  // RECIPE_RESOLVED gate: work units with no resolved recipe block POR (Canon Doc 21 §5.1)
  const recipePending = workUnits.filter((w) => !w.resolved_recipe_id);
  if (recipePending.length > 0) {
    results.push({
      check_key: 'recipe.pending',
      result: 'fail',
      payload: {
        severity: 0.9,
        explanation: `${recipePending.length} work unit(s) have no work type, so how the work is approached is unknown. Assign a work type on each item.`,
        remediation: 'Item detail → Work Type selector',
        missing_input: 'Assign Work Type',
        target_refs: recipePending.map((w) => w.id),
      },
    });
  }

  const missingEstimate = workUnits.filter((w) => {
    const v = w.estimated_effort_hours;
    return v === null || v === undefined || Number(v) <= 0;
  });
  if (missingEstimate.length === 0 && results.length === 0) {
    return [{ check_key: CHECK_ASSUMPTIONS_VALID, result: 'pass', payload: { explanation: 'All work units have estimated effort > 0 and resolved recipes' } }];
  }
  if (missingEstimate.length > 0) {
    results.push({
      check_key: CHECK_ASSUMPTIONS_VALID,
      result: 'fail',
      payload: {
        severity: 0.6,
        explanation: `${missingEstimate.length} work unit(s) missing or invalid estimated effort (must be > 0)`,
        remediation: 'Set estimated_effort_hours > 0 for each work unit (Doc 19 §5.3).',
        target_refs: missingEstimate.map((w) => w.id),
      },
    });
  }
  return results;
}

/** 6) Eligibility constraints (Doc 19 §5). Minimum: every initiative must have at least one work unit to be eligible for execution. */
async function runEligibilityChecks(planVersionId: string): Promise<ConstraintCheckResult[]> {
  const supabase = getSupabaseClient();
  const { data: initRows } = await supabase.from('initiatives').select('id, title').eq('plan_version_id', planVersionId);
  const initiatives = (initRows ?? []) as { id: string; title: string | null }[];
  if (initiatives.length === 0) {
    return [{ check_key: CHECK_INITIATIVES_NOT_DROPPED, result: 'pass', payload: { explanation: 'No initiatives; eligibility N/A' } }];
  }
  const { data: wuRows } = await supabase.from('work_units').select('initiative_id').in('initiative_id', initiatives.map((i) => i.id));
  const wuByInit = new Map<string, number>();
  for (const i of initiatives) wuByInit.set(i.id, 0);
  for (const w of (wuRows ?? []) as { initiative_id: string }[]) {
    wuByInit.set(w.initiative_id, (wuByInit.get(w.initiative_id) ?? 0) + 1);
  }
  const ineligible = initiatives.filter((i) => (wuByInit.get(i.id) ?? 0) === 0);
  if (ineligible.length === 0) {
    return [{ check_key: CHECK_INITIATIVES_NOT_DROPPED, result: 'pass', payload: { explanation: 'All initiatives have at least one work unit' } }];
  }
  return [
    {
      check_key: CHECK_INITIATIVES_NOT_DROPPED,
      result: 'fail',
      payload: {
        severity: 0.5,
        explanation: `${ineligible.length} initiative(s) have no work units and are ineligible for execution`,
        remediation: 'Add at least one work unit to each initiative or remove empty initiatives from the plan.',
        target_refs: ineligible.map((i) => i.id),
      },
    },
  ];
}

/**
 * 0) The floor: a plan version must contain something.
 *
 * Every other check reads the version's contents, so an empty version passes
 * them all vacuously and arrives at authorization with a clean bill of health.
 * Observed live: a version committed from the engine carried 0 initiatives and
 * scored 6 pass / 0 violations, while the version it replaced — 18 initiatives,
 * no work units — failed honestly.
 */
async function runVersionNotEmptyChecks(planVersionId: string): Promise<ConstraintCheckResult[]> {
  const supabase = getSupabaseClient();
  const { data: initRows } = await supabase
    .from('initiatives')
    .select('id')
    .eq('plan_version_id', planVersionId);
  const initiativeCount = (initRows ?? []).length;
  if (initiativeCount > 0) {
    return [
      {
        check_key: CHECK_VERSION_NOT_EMPTY,
        result: 'pass',
        payload: { explanation: `${initiativeCount} initiative(s) in this version` },
      },
    ];
  }
  return [
    {
      check_key: CHECK_VERSION_NOT_EMPTY,
      result: 'fail',
      payload: {
        severity: 1,
        explanation: 'This plan version contains no initiatives, so there is nothing to authorize',
        remediation:
          'Propose a version from a scenario that has priorities in scope, or add initiatives to this version.',
      },
    },
  ];
}

export interface ConstraintEvaluationResult {
  checks: ConstraintCheckResult[];
  summary: { pass: number; warn: number; fail: number };
  details: {
    evaluated_at: string;
    constraint_set: string[];
    results: Record<string, { result: string; payload?: Record<string, unknown> }>;
    violations: Array<{ check_key: string; severity?: number; remediation?: string }>;
    explainability: { narratives: string[] };
  };
}

/**
 * Run full constraint evaluation in Doc 19 §5.1 order. Does not persist.
 */
export async function runConstraintEvaluation(planVersionId: string): Promise<{
  data: ConstraintEvaluationResult | null;
  error: string | null;
}> {
  try {
    const checks: ConstraintCheckResult[] = [];
    const order: (() => Promise<ConstraintCheckResult[]>)[] = [
      () => runVersionNotEmptyChecks(planVersionId),
      () => runStructuralChecks(planVersionId),
      () => runDependencyFeasibilityChecks(planVersionId),
      () => runCapabilityFitChecks(planVersionId),
      () => runCapacityFeasibilityChecks(planVersionId),
      () => runPolicyChecks(planVersionId),
      () => runEligibilityChecks(planVersionId),
    ];
    for (const run of order) {
      const batch = await run();
      checks.push(...batch);
    }
    const pass = checks.filter((c) => c.result === 'pass').length;
    const warn = checks.filter((c) => c.result === 'warn').length;
    const fail = checks.filter((c) => c.result === 'fail').length;
    const results: Record<string, { result: string; payload?: Record<string, unknown> }> = {};
    checks.forEach((c, i) => {
      results[c.check_key + (results[c.check_key] ? `.${i}` : '')] = { result: c.result, payload: c.payload as Record<string, unknown> };
    });
    const violations = checks.filter((c) => c.result === 'fail').map((c) => ({
      check_key: c.check_key,
      severity: c.payload?.severity,
      remediation: c.payload?.remediation,
    }));
    const narratives = checks.filter((c) => c.result !== 'pass').map((c) => `${c.check_key}: ${c.payload?.explanation ?? c.result}`);
    return {
      data: {
        checks,
        summary: { pass, warn, fail },
        details: {
          evaluated_at: new Date().toISOString(),
          constraint_set: ['structural', 'dependency', 'capability', 'capacity', 'policy', 'eligibility'],
          results,
          violations,
          explainability: { narratives },
        },
      },
      error: null,
    };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Constraint evaluation failed' };
  }
}

// ---------------------------------------------------------------------------
// 6. Divergence detection (Doc 19 §6)
// ---------------------------------------------------------------------------

export interface DivergencePayload {
  divergence_kind: string;
  higher_item_id?: string;
  higher_ranked_title: string;
  higher_rank?: number;
  lower_item_id?: string;
  lower_ranked_title: string;
  lower_rank?: number;
  explanation: string;
}

/**
 * What this version shows that differs from its rank (Doc 19 §6.2). The
 * database holds the one reading, so authorize_por and this agree.
 */
export async function runDivergenceDetection(planVersionId: string): Promise<{
  data: DivergencePayload[];
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.rpc('plan_version_divergences', {
      p_plan_version_id: planVersionId,
    });
    if (error) return { data: [], error: error.message };
    const rows = (data ?? []) as Array<{ payload: DivergencePayload }>;
    return { data: rows.map((r) => r.payload), error: null };
  } catch (e) {
    return { data: [], error: e instanceof Error ? e.message : 'Divergence detection failed' };
  }
}

// ---------------------------------------------------------------------------
// 7. Scenario scoring (Doc 19 §7) — minimal vector
// ---------------------------------------------------------------------------

export interface ScenarioScoreVector {
  feasibility_score: number;
  divergence_penalty: number;
  risk_penalty: number;
  value_proxy?: number;
  score_summary: Record<string, number>;
  engine_version: string;
}

/**
 * Compute scenario score vector for a plan version (Doc 19 §7). Deterministic.
 */
export async function computeScenarioScore(
  planVersionId: string,
  options?: { evaluationResult?: ConstraintEvaluationResult; divergenceCount?: number }
): Promise<{ data: ScenarioScoreVector | null; error: string | null }> {
  try {
    const evalResult = options?.evaluationResult ?? (await runConstraintEvaluation(planVersionId)).data;
    const divCount = options?.divergenceCount ?? (await runDivergenceDetection(planVersionId)).data.length;
    const failCount = evalResult?.summary.fail ?? 0;
    const warnCount = evalResult?.summary.warn ?? 0;
    const feasibility_score = Math.max(0, 1 - failCount * 0.3 - warnCount * 0.1);
    const divergence_penalty = Math.min(1, divCount * 0.2);
    const supabase = getSupabaseClient();
    const { data: riskRows } = await supabase.from('risks').select('impact_score, likelihood_score').eq('plan_version_id', planVersionId);
    const risks = (riskRows ?? []) as { impact_score: number | null; likelihood_score: number | null }[];
    const risk_penalty = Math.min(1, risks.reduce((s, r) => s + (Number(r.impact_score) || 0) * (Number(r.likelihood_score) || 0) * 0.01, 0));
    return {
      data: {
        feasibility_score,
        divergence_penalty,
        risk_penalty,
        score_summary: { feasibility_score, divergence_penalty, risk_penalty },
        engine_version: ENGINE_VERSION,
      },
      error: null,
    };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Scenario score failed' };
  }
}

// ---------------------------------------------------------------------------
// Persist evaluation (RPC + constraint_check_results + summary/details)
// ---------------------------------------------------------------------------

/**
 * Run constraint evaluation and persist atomically via a single RPC (Doc 19 §5, §8).
 * Evaluation input is the plan version snapshot only; no prioritization reads.
 * RPC inserts plan_constraint_evaluations (with summary/details), constraint_check_results, and audit in one transaction.
 */
export async function runAndPersistConstraintEvaluation(planVersionId: string, options?: { evaluated_by?: string | null }): Promise<{
  data: { evaluationId: string } | null;
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const { data: pv, error: pvErr } = await supabase
      .from('plan_versions')
      .select('id')
      .eq('id', planVersionId)
      .maybeSingle();
    if (pvErr) return { data: null, error: pvErr.message };
    if (!pv) return { data: null, error: 'Plan version not found. The active POR may reference a version that was removed or that you don\'t have access to.' };

    const evalOut = await runConstraintEvaluation(planVersionId);
    if (evalOut.error || !evalOut.data) return { data: null, error: evalOut.error ?? 'No evaluation result' };

    const rpcRes = await evaluatePlanVersionRpc({
      plan_version_id: planVersionId,
      engine_version: ENGINE_VERSION,
      evaluated_by: options?.evaluated_by,
      summary: evalOut.data.summary as unknown as Record<string, unknown>,
      details: evalOut.data.details as unknown as Record<string, unknown>,
      checks: evalOut.data.checks.map((c) => ({
        check_key: c.check_key,
        result: c.result,
        payload: c.payload ?? {},
      })),
    });
    if (rpcRes.error || !rpcRes.data) return { data: null, error: rpcRes.error ?? 'RPC failed' };

    // Emit EVALUATION_COMPUTED audit event (Canon Doc 14 §4)
    const supabase2 = getSupabaseClient();
    await supabase2.from('planning_audit_events').insert({
      event_type: 'EVALUATION_COMPUTED',
      plan_version_id: planVersionId,
      payload: {
        evaluation_id: rpcRes.data,
        summary: evalOut.data.summary,
        engine_version: ENGINE_VERSION,
        evaluated_by: options?.evaluated_by ?? null,
      },
    }).then(() => {}).catch(() => {});

    return { data: { evaluationId: rpcRes.data }, error: null };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Run and persist evaluation failed' };
  }
}
